import { useRef } from 'react'
import { INVENTORY_CLIENT_URL } from 'config'
import { useAccessToken, useMyAccount, usePartners } from 'hooks'
import { useTranslation } from 'react-i18next'
import { useStyles } from './index.styles'
import { CountryCodes, Spinner } from '@audibene/react-components-lib'
import { EventType } from '../../hooks/useWindowMessage'
import { usePartnerEmployeePartnerShops } from 'hooks/usePartnerEmployeePartnerShops'

type DocumentClientUrlDeps = {
  countryCode: string
  locationGid: string
  partnerShopSalesforceId: string
}

const makeInventoryManagementSystemClientUrl = ({
  countryCode,
  locationGid,
  partnerShopSalesforceId,
}: DocumentClientUrlDeps) => {
  const url = new URL(INVENTORY_CLIENT_URL)

  url.searchParams.set('countryCode', countryCode)
  url.searchParams.set('locationGid', locationGid)
  url.searchParams.set('partnerShopSalesforceId', partnerShopSalesforceId)
  url.searchParams.set('shouldInjectAccessToken', 'true')
  return url.href
}

const InventoryManagementSystemClient = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const classes = useStyles()
  const { data: myAccount, isLoading: isMyAccountLoading } = useMyAccount()
  const { data: partner, isLoading: isPartnerLoading } = usePartners({
    id: myAccount?.id,
  })
  const { data: partnerEmployeePartnerShops, isLoading: isPartnerEmployeePartnerShopsLoading } =
    usePartnerEmployeePartnerShops(
      { gid: partner?.[0]?.gid ?? '' },
      {
        enabled: !!partner?.[0]?.gid,
      }
    )

  // TODO: This is a temporary solution to get the primary partner shop, in the future we should get this info from backend
  const primaryPartnerShop = partnerEmployeePartnerShops?.[0]

  const { t } = useTranslation()
  const { data: accessToken } = useAccessToken()

  const iframeSrcUrl = makeInventoryManagementSystemClientUrl({
    countryCode: CountryCodes.DE,
    locationGid: primaryPartnerShop?.gid ?? '',
    partnerShopSalesforceId: primaryPartnerShop?.salesforceId ?? '',
  })

  const handlePost = () => {
    const isAbleToPostMessage = iframeRef.current?.contentWindow

    if (!isAbleToPostMessage) {
      return
    }

    iframeRef.current.contentWindow.postMessage(
      { payload: { accessToken }, type: EventType.SET_ACCESS_TOKEN },
      INVENTORY_CLIENT_URL
    )
  }

  if (isMyAccountLoading || isPartnerLoading || isPartnerEmployeePartnerShopsLoading) {
    return <Spinner />
  }

  return (
    <iframe
      className={classes.iframe}
      loading="lazy"
      onLoad={handlePost}
      ref={iframeRef}
      src={iframeSrcUrl}
      title={t('customer_profile.tabs.documents.document_client_iframe_title')}
    />
  )
}

export { InventoryManagementSystemClient }
